import Axios, { Method } from "axios";
import SwalAlertComp from "components/SwalAlert";
import { baseURL } from "configurations/environment";
import { RequestConfig } from "helpers/RequestConfig";

/**
 *
 * @param {Array<string | number>} arr
 * @returns cleaned array
 */
const clearDupplicates = (arr) =>
    arr?.reduce((acc, cur) => {
        !acc?.includes(cur) && acc?.push(cur);
        return acc;
    }, []);

/**
 * @param {unknown} error
 * @returns {Promise<string>}
 */
const readBlobErrorMessage = async (error) => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const text = await data.text();
            const json = JSON.parse(text);
            return json?.message || "An error occurred";
        } catch {
            return "An error occurred";
        }
    }
    return data?.message || error?.message || "An error occurred";
};

/**
 * @description createRequest helper for creating request with Axios. It abstracts some logic and return specific payload.
 * @param {object} options
 * @param {string} options.suffix Path after `/api/`.
 * @param {Method} options.method
 * @param {any} [options.payload]
 * @param {boolean} [options.handleError]
 * @param {Record<string, any>} [options.params]
 * @param {import("axios").AxiosRequestConfig} [options.config]
 * @returns {Promise<{ status: boolean, message?: string, data?: any, headers?: import("axios").AxiosResponse["headers"] }>}
 * @example export const getMetaCustomAudiences = async (payload) => {
    await createRequest({
        handleError: true,
        method: "post",
        payload,
        suffix: "destinations/fb/ca/fetch",
    });};
 */
const createRequest = async ({
    suffix,
    method,
    payload,
    handleError,
    params,
    config: extraConfig,
}) => {
    try {
        let requestURL = baseURL + `/api/${suffix}`;
        const config = RequestConfig({
            ...(params ? { params } : {}),
            ...extraConfig,
        });
        const handlers = [
            {
                predicate: () => method === "get",
                handler: () => Axios.get(requestURL, config),
            },
            {
                predicate: () => method === "post",
                handler: () => Axios.post(requestURL, payload, config),
            },
            {
                predicate: () => method === "put",
                handler: () => Axios.put(requestURL, payload, config),
            },
            {
                predicate: () => method === "delete",
                handler: () => Axios.delete(requestURL, config),
            },
        ];
        const response = await handlers
            .find(({ predicate }) => predicate())
            ?.handler();

        const isBlob = extraConfig?.responseType === "blob";
        return {
            status: true,
            message: isBlob ? undefined : response?.data?.message,
            data: isBlob ? response?.data : response?.data?.data,
            headers: isBlob ? response?.headers : undefined,
        };
    } catch (error) {
        const message = await readBlobErrorMessage(error);
        handleError &&
            SwalAlertComp({
                title: "Failed",
                message,
                icon: "error",
            });
        return {
            status: false,
            message,
        };
    }
};

export { createRequest, clearDupplicates };
